import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import apiService, { Product } from '../../services/api';
import ImageUpload, { ProductImage } from './ImageUpload';
import { toImageSrc, toThumbSrc } from '../../services/api';

const ProductsWrapper = styled.div`
  background: white;
  border-radius: var(--border-radius-lg);
  box-shadow: var(--shadow-sm);
  overflow: hidden;
`;

const ProductsHeader = styled.div`
  padding: var(--spacing-6);
  border-bottom: 1px solid var(--color-border);
  display: flex;
  justify-content: space-between;
  align-items: center;
  
  h2 {
    margin: 0;
    color: var(--color-secondary);
  }
`;

const AddButton = styled.button`
  background-color: var(--color-secondary);
  color: white;
  padding: var(--spacing-3) var(--spacing-4);
  border: none;
  border-radius: var(--border-radius);
  cursor: pointer;
  font-weight: 500;
  
  &:hover {
    background-color: #1f1e3a;
  }
`;

const FilterSection = styled.div`
  padding: var(--spacing-4) var(--spacing-6);
  background-color: #f9fafb;
  border-bottom: 1px solid var(--color-border);
  display: flex;
  gap: var(--spacing-4);
  align-items: center;
  
  input, select {
    padding: var(--spacing-2) var(--spacing-3);
    border: 1px solid var(--color-border);
    border-radius: var(--border-radius);
    font-size: var(--font-size-sm);
  }
`;

const ProductTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  
  th {
    background-color: #f9fafb;
    padding: var(--spacing-4) var(--spacing-6);
    text-align: left;
    font-weight: 500;
    color: var(--color-secondary);
    border-bottom: 1px solid var(--color-border);
  }
  
  td {
    padding: var(--spacing-4) var(--spacing-6);
    border-bottom: 1px solid var(--color-border);
  }
  
  tr:hover {
    background-color: #f9fafb;
  }
`;

const ProductImageThumbnail = styled.div`
  width: 50px;
  height: 50px;
  background-color: var(--color-primary);
  border-radius: var(--border-radius);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  overflow: hidden;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const StatusBadge = styled.span<{ status: 'active' | 'inactive' }>`
  padding: var(--spacing-1) var(--spacing-2);
  border-radius: var(--border-radius);
  font-size: var(--font-size-xs);
  font-weight: 500;
  background-color: ${props => props.status === 'active' ? '#dcfce7' : '#fee2e2'};
  color: ${props => props.status === 'active' ? '#166534' : '#991b1b'};
`;

const ActionButtons = styled.div`
  display: flex;
  gap: var(--spacing-2);
`;

const ActionButton = styled.button`
  padding: var(--spacing-1) var(--spacing-2);
  border: none;
  border-radius: var(--border-radius);
  cursor: pointer;
  font-size: var(--font-size-sm);
  
  &.edit {
    background-color: #dbeafe;
    color: #1d4ed8;
  }
  
  &.delete {
    background-color: #fee2e2;
    color: #dc2626;
  }
  
  &:hover {
    opacity: 0.8;
  }
`;

const ProductForm = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const FormContent = styled.div`
  background: white;
  padding: var(--spacing-6);
  border-radius: var(--border-radius-lg);
  width: 90%;
  max-width: 700px;
  max-height: 90vh;
  overflow-y: auto;
  
  h3 {
    color: var(--color-secondary);
    margin-bottom: var(--spacing-4);
  }
  
  .form-row {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: var(--spacing-4);
    
    @media (max-width: 768px) {
      grid-template-columns: 1fr;
    }
  }
  
  .form-group {
    margin-bottom: var(--spacing-4);
    
    label {
      display: block;
      margin-bottom: var(--spacing-2);
      font-weight: 500;
      color: var(--color-text);
    }
    
    input, textarea, select {
      width: 100%;
      padding: var(--spacing-3);
      border: 1px solid var(--color-border);
      border-radius: var(--border-radius);
      font-size: var(--font-size-base);
      
      &:focus {
        outline: none;
        border-color: var(--color-secondary);
      }
    }
    
    textarea {
      height: 100px;
      resize: vertical;
    }
  }
  
  .margin-display {
    background-color: #f0f9ff;
    border: 1px solid #bfdbfe;
    border-radius: var(--border-radius);
    padding: var(--spacing-3);
    margin-top: var(--spacing-2);
    font-size: var(--font-size-sm);
    
    .margin-item {
      display: flex;
      justify-content: space-between;
      margin-bottom: var(--spacing-1);
      
      &:last-child {
        margin-bottom: 0;
        font-weight: 500;
        border-top: 1px solid #e5e7eb;
        padding-top: var(--spacing-2);
      }
    }
  }
  
  .error-message {
    color: var(--color-error);
    font-size: var(--font-size-sm);
    margin-top: var(--spacing-1);
  }
  
  .sku-suggestions {
    margin-top: var(--spacing-2);
    
    .suggestions-label {
      font-size: var(--font-size-sm);
      color: var(--color-text-light);
      margin-bottom: var(--spacing-2);
      font-weight: 500;
    }
    
    .suggestions-list {
      display: flex;
      flex-wrap: wrap;
      gap: var(--spacing-2);
    }
    
    .suggestion-item {
      background: #e0f2fe;
      border: 1px solid #0284c7;
      border-radius: var(--border-radius);
      padding: var(--spacing-1) var(--spacing-2);
      font-size: var(--font-size-sm);
      color: #0369a1;
      cursor: pointer;
      transition: all 0.2s ease;
      
      &:hover {
        background: #0284c7;
        color: white;
      }
      
      &.selected {
        background: #0284c7;
        color: white;
      }
    }
  }
  
  .sku-status {
    margin-top: var(--spacing-1);
    font-size: var(--font-size-sm);
    display: flex;
    align-items: center;
    gap: var(--spacing-1);
    
    &.checking {
      color: var(--color-text-light);
    }
    
    &.available {
      color: #16a34a;
    }
    
    &.unavailable {
      color: var(--color-error);
    }
  }
  
  .form-actions {
    display: flex;
    justify-content: flex-end;
    gap: var(--spacing-3);
    margin-top: var(--spacing-6);
    
    button {
      padding: var(--spacing-3) var(--spacing-4);
      border: none;
      border-radius: var(--border-radius);
      cursor: pointer;
      font-weight: 500;
      
      &:disabled {
        opacity: 0.6;
        cursor: not-allowed;
      }
      
      &.cancel {
        background: var(--color-border);
        color: var(--color-text);
        
        &:hover:not(:disabled) {
          background: #d1d5db;
        }
      }
      
      &.save {
        background: var(--color-secondary);
        color: white;
        
        &:hover:not(:disabled) {
          background: #1f1e3a;
        }
      }
    }
  }
`;


interface FormData {
  name: string;
  slug: string;
  description: string;
  short_description: string;
  price: string;
  compare_price: string;
  cost_price: string;
  sku: string;
  inventory_quantity: string;
  category_ids: number[];
  is_active: boolean;
  is_featured: boolean;
  images: ProductImage[];
}

interface Category {
  id: number;
  name: string;
  slug: string;
}

const AdminProducts: React.FC = () => {
  const [products, setProducts] = useState<Product[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [formData, setFormData] = useState<FormData>({
    name: '',
    slug: '',
    description: '',
    short_description: '',
    price: '',
    compare_price: '',
    cost_price: '',
    sku: '',
    inventory_quantity: '',
    category_ids: [],
    is_active: true,
    is_featured: false,
    images: []
  });
  const [formErrors, setFormErrors] = useState<{[key: string]: string}>({});
  const [suggestedSkus, setSuggestedSkus] = useState<string[]>([]);
  const [checkingSku, setCheckingSku] = useState(false);

  const getProductEmoji = (categories?: string[]) => {
    if (!categories || categories.length === 0) return '🏺';
    if (categories.some(cat => cat.toLowerCase().includes('ljus'))) return '🕯️';
    if (categories.some(cat => cat.toLowerCase().includes('godis'))) return '🍬';
    if (categories.some(cat => cat.toLowerCase().includes('cottage'))) return '☕';
    return '🏺';
  };

  const generateSkuSuggestions = (productName: string, categoryIds: number[]): string[] => {
    if (!productName.trim()) return [];
    
    const suggestions: string[] = [];
    const name = productName.trim().toUpperCase();
    
    // Get category prefix
    let categoryPrefix = '';
    if (categoryIds.length > 0) {
      const category = categories.find(cat => cat.id === categoryIds[0]);
      if (category) {
        const categoryName = category.name.toLowerCase();
        if (categoryName.includes('ljus')) categoryPrefix = 'CANDLE';
        else if (categoryName.includes('godis')) categoryPrefix = 'CANDY';
        else if (categoryName.includes('cottage')) categoryPrefix = 'MAYA';
        else if (categoryName.includes('dekoration')) categoryPrefix = 'DECO';
        else categoryPrefix = 'PROD';
      }
    } else {
      categoryPrefix = 'PROD';
    }
    
    // Method 1: Category + First letters of each word + number
    const words = name.split(' ').filter(word => word.length > 0);
    const firstLetters = words.map(word => word.charAt(0)).join('').substring(0, 3);
    suggestions.push(`${categoryPrefix}-${firstLetters}-001`);
    
    // Method 2: Category + First 3 letters of first word + number  
    if (words.length > 0) {
      const firstWord = words[0].substring(0, 3);
      suggestions.push(`${categoryPrefix}-${firstWord}-001`);
    }
    
    // Method 3: Category + Full first word (max 6 chars) + number
    if (words.length > 0) {
      const firstWord = words[0].substring(0, 6);
      suggestions.push(`${categoryPrefix}-${firstWord}-001`);
    }
    
    // Remove duplicates and filter out existing SKUs
    const uniqueSuggestions = suggestions.filter((sku, index) => suggestions.indexOf(sku) === index);
    return uniqueSuggestions.filter(sku => !products.some(p => p.sku === sku));
  };

  const checkSkuAvailability = async (sku: string): Promise<boolean> => {
    if (!sku.trim()) return true;
    
    // Check against current products list
    const isExisting = products.some(product => 
      product.sku.toLowerCase() === sku.toLowerCase() && 
      product.id !== editingProduct?.id
    );
    
    return !isExisting;
  };

  const validateSku = async (sku: string) => {
    setCheckingSku(true);
    const isAvailable = await checkSkuAvailability(sku);
    setCheckingSku(false);
    
    if (!isAvailable) {
      setFormErrors(prev => ({
        ...prev,
        sku: 'Detta SKU används redan av en annan produkt'
      }));
      return false;
    } else {
      setFormErrors(prev => {
        const updated = { ...prev };
        delete updated.sku;
        return updated;
      });
      return true;
    }
  };

  useEffect(() => {
    fetchProducts();
    fetchCategories();
  }, []);

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const data = await apiService.getProducts();
      setProducts(data);
    } catch (error) {
      console.error('Error fetching products:', error);
      // Fallback data
      setProducts([
        { id: 1, name: 'Lavendel Doftljus', slug: 'lavendel-doftljus', price: 149, cost_price: 89, sku: 'CANDLE-LAV-001', inventory_quantity: 25, is_active: true, categories: ['Ljus & Doftljus'], created_at: '2024-01-01', description: '', short_description: '', is_featured: false },
        { id: 2, name: 'Vintage Tekopp Set', slug: 'vintage-tekopp-set', price: 299, cost_price: 180, sku: 'MAYA-TEA-001', inventory_quantity: 15, is_active: true, categories: ['Mayas Cottage'], created_at: '2024-01-01', description: '', short_description: '', is_featured: false },
        { id: 3, name: 'Gourmet Choklad', slug: 'gourmet-choklad', price: 89, cost_price: 45, sku: 'CANDY-CHOC-001', inventory_quantity: 50, is_active: true, categories: ['Godis & Snacks'], created_at: '2024-01-01', description: '', short_description: '', is_featured: false },
        { id: 4, name: 'Keramik Vas', slug: 'keramik-vas', price: 199, cost_price: 120, sku: 'DECO-VASE-001', inventory_quantity: 20, is_active: true, categories: ['Dekorationer'], created_at: '2024-01-01', description: '', short_description: '', is_featured: false }
      ]);
    }
    setLoading(false);
  };

  const fetchCategories = async () => {
    try {
      const data = await apiService.getCategories();
      setCategories(data);
    } catch (error) {
      console.error('Error fetching categories:', error);
      // Fallback data
      setCategories([
        { id: 1, name: 'Ljus & Doftljus', slug: 'ljus' },
        { id: 2, name: 'Godis & Snacks', slug: 'godis' },
        { id: 3, name: 'Mayas Cottage', slug: 'mayas-cottage' },
        { id: 4, name: 'Dekorationer', slug: 'dekorationer' }
      ]);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      slug: '',
      description: '',
      short_description: '',
      price: '',
      compare_price: '',
      cost_price: '',
      sku: '',
      inventory_quantity: '',
      category_ids: [],
      is_active: true,
      is_featured: false,
      images: []
    });
    setFormErrors({});
    setSuggestedSkus([]);
    setCheckingSku(false);
  };

  const handleEdit = (product: Product) => {
    setEditingProduct(product);
    
    // Convert product images to form format
    const productImages: ProductImage[] = (product.images || []).map((imageUrl, index) => ({
      url: imageUrl.startsWith('http') ? imageUrl : `https://lillaflora.nu${imageUrl}`,
      alt_text: product.name,
      is_primary: index === 0, // First image is primary, others false
      uploading: false
    }));
    
    setFormData({
      name: product.name,
      slug: product.slug,
      description: product.description || '',
      short_description: product.short_description || '',
      price: product.price.toString(),
      compare_price: product.compare_price?.toString() || '',
      cost_price: product.cost_price?.toString() || '',
      sku: product.sku,
      inventory_quantity: product.inventory_quantity.toString(),
      category_ids: product.category_ids || [],
      is_active: product.is_active,
      is_featured: product.is_featured,
      images: productImages
    });
    setShowForm(true);
  };

  const handleAdd = () => {
    setEditingProduct(null);
    resetForm();
    setShowForm(true);
  };

  const handleDelete = async (product: Product) => {
    if (window.confirm(`Är du säker på att du vill ta bort "${product.name}"?\n\nDenna åtgärd går inte att ångra.`)) {
      try {
        await apiService.deleteProduct(product.id!);
        await fetchProducts(); // Refresh the list
        alert('Produkten har tagits bort framgångsrikt!');
      } catch (error) {
        console.error('Error deleting product:', error);
        alert('Ett fel uppstod när produkten skulle tas bort: ' + (error as Error).message);
      }
    }
  };

  const validateForm = async (): Promise<boolean> => {
    const errors: {[key: string]: string} = {};

    if (!formData.name.trim()) {
      errors.name = 'Produktnamn är obligatoriskt';
    }

    if (!formData.slug.trim()) {
      errors.slug = 'Slug är obligatorisk';
    }

    if (!formData.price || parseFloat(formData.price) <= 0) {
      errors.price = 'Försäljningspris måste vara större än 0';
    }

    if (!formData.sku.trim()) {
      errors.sku = 'SKU är obligatorisk';
    } else {
      // Check if SKU is already used
      const isAvailable = await checkSkuAvailability(formData.sku);
      if (!isAvailable) {
        errors.sku = 'Detta SKU används redan av en annan produkt';
      }
    }

    if (!formData.inventory_quantity || parseInt(formData.inventory_quantity) < 0) {
      errors.inventory_quantity = 'Lagerantal måste vara 0 eller högre';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const isValid = await validateForm();
    if (!isValid) {
      return;
    }

    setSaving(true);
    
    try {
      // Extract image URLs from the uploaded images
      console.log('FormData images:', formData.images);
      const imageUrls = formData.images
        .filter(img => !img.uploading && img.url) // Only include successfully uploaded images
        .map(img => img.url);
      console.log('Extracted imageUrls:', imageUrls);

      const productData = {
        name: formData.name,
        slug: formData.slug,
        description: formData.description,
        short_description: formData.short_description,
        price: parseFloat(formData.price),
        compare_price: formData.compare_price ? parseFloat(formData.compare_price) : undefined,
        cost_price: formData.cost_price ? parseFloat(formData.cost_price) : undefined,
        sku: formData.sku,
        inventory_quantity: parseInt(formData.inventory_quantity),
        category_ids: formData.category_ids,
        is_active: formData.is_active,
        is_featured: formData.is_featured,
        images: imageUrls
      };
      
      console.log('Sending product data to API:', productData);

      if (editingProduct) {
        await apiService.updateProduct(editingProduct.id!, productData);
        alert('Produkten har uppdaterats framgångsrikt!');
      } else {
        await apiService.createProduct(productData);
        alert('Produkten har skapats framgångsrikt!');
      }

      await fetchProducts(); // Refresh the list
      setShowForm(false);
      setEditingProduct(null);
      resetForm();
    } catch (error) {
      console.error('Error saving product:', error);
      alert('Ett fel uppstod när produkten skulle sparas: ' + (error as Error).message);
    } finally {
      setSaving(false);
    }
  };

  const handleFormChange = (field: keyof FormData, value: any) => {
    setFormData(prev => {
      const updated = { ...prev, [field]: value };
      
      // Auto-generate slug from name
      if (field === 'name') {
        updated.slug = apiService.generateSlug(value);
        console.log('Generated slug from name:', value, '=>', updated.slug);
        
        // Generate SKU suggestions when name changes (only for new products)
        if (!editingProduct && value.trim()) {
          const suggestions = generateSkuSuggestions(value, prev.category_ids);
          setSuggestedSkus(suggestions);
          // Auto-fill first suggestion if SKU is empty
          if (!prev.sku && suggestions.length > 0) {
            updated.sku = suggestions[0];
          }
        }
      }
      
      // Update SKU suggestions when category changes
      if (field === 'category_ids' && !editingProduct && updated.name.trim()) {
        const suggestions = generateSkuSuggestions(updated.name, value);
        setSuggestedSkus(suggestions);
      }
      
      return updated;
    });

    // Clear error for this field
    if (formErrors[field]) {
      setFormErrors(prev => {
        const updated = { ...prev };
        delete updated[field];
        return updated;
      });
    }
    
    // Validate SKU when it changes
    if (field === 'sku' && value.trim()) {
      setTimeout(() => validateSku(value), 300); // Debounce validation
    }
  };

  const handleImagesChange = (imagesOrUpdater: ProductImage[] | ((prev: ProductImage[]) => ProductImage[])) => {
    if (typeof imagesOrUpdater === 'function') {
      // Handle callback pattern
      setFormData(prev => {
        const newImages = imagesOrUpdater(prev.images);
        console.log('handleImagesChange called with updater, result:', newImages);
        const updated = { ...prev, images: newImages };
        console.log('Updated formData with images:', updated.images);
        return updated;
      });
    } else {
      // Handle direct array
      console.log('handleImagesChange called with:', imagesOrUpdater);
      setFormData(prev => {
        const updated = { ...prev, images: imagesOrUpdater };
        console.log('Updated formData with images:', updated.images);
        return updated;
      });
    }
  };

  const calculateMargins = () => {
    const price = parseFloat(formData.price);
    const costPrice = parseFloat(formData.cost_price);
    
    if (!price || !costPrice || price <= 0 || costPrice <= 0) {
      return null;
    }

    const margin = apiService.calculateMargin(price, costPrice);
    const markup = apiService.calculateMarkup(price, costPrice);
    const profit = price - costPrice;

    return { margin, markup, profit };
  };

  const filteredProducts = products.filter(product =>
    product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    product.sku.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <ProductsWrapper>
      <ProductsHeader>
        <h2>Produkthantering</h2>
        <AddButton onClick={handleAdd}>
          + Lägg till produkt
        </AddButton>
      </ProductsHeader>
      
      <FilterSection>
        <input
          type="text"
          placeholder="Sök produkter..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        <select>
          <option value="">Alla kategorier</option>
          <option value="ljus">Ljus & Doftljus</option>
          <option value="godis">Godis & Snacks</option>
          <option value="cottage">Mayas Cottage</option>
          <option value="deko">Dekorationer</option>
        </select>
      </FilterSection>
      
      {loading ? (
        <div style={{ padding: 'var(--spacing-8)', textAlign: 'center' }}>
          Laddar produkter...
        </div>
      ) : (
        <ProductTable>
          <thead>
            <tr>
              <th>Produkt</th>
              <th>SKU</th>
              <th>Pris</th>
              <th>Marginal</th>
              <th>Lager</th>
              <th>Status</th>
              <th>Åtgärder</th>
            </tr>
          </thead>
          <tbody>
            {filteredProducts.map(product => (
              <tr key={product.id}>
                <td>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-3)' }}>
                    <ProductImageThumbnail>
                      {product.images && product.images.length > 0 ? (
                        <img
                          src={toThumbSrc(product.images[0])}
                          alt={product.name}
                          onError={(e) => {
                            const target = e.currentTarget as HTMLImageElement;
                            target.onerror = null;
                            target.src = toImageSrc(product.images![0]);
                          }}
                        />
                      ) : (
                        getProductEmoji(product.categories)
                      )}
                    </ProductImageThumbnail>
                    <div>
                      <div style={{ fontWeight: '500' }}>{product.name}</div>
                      <div style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-light)' }}>
                        {product.categories?.join(', ') || 'Ingen kategori'}
                      </div>
                    </div>
                  </div>
                </td>
                <td>{product.sku}</td>
                <td>{product.price} kr</td>
                <td>
                  {product.cost_price && product.cost_price > 0 ? (
                    <span style={{ 
                      color: apiService.calculateMargin(product.price, product.cost_price) >= 30 ? '#16a34a' : 
                             apiService.calculateMargin(product.price, product.cost_price) >= 15 ? '#ea580c' : '#dc2626',
                      fontWeight: '500'
                    }}>
                      {apiService.calculateMargin(product.price, product.cost_price)}%
                    </span>
                  ) : (
                    <span style={{ color: 'var(--color-text-light)', fontSize: 'var(--font-size-sm)' }}>
                      N/A
                    </span>
                  )}
                </td>
                <td>{product.inventory_quantity} st</td>
                <td>
                  <StatusBadge status={product.is_active ? 'active' : 'inactive'}>
                    {product.is_active ? 'Aktiv' : 'Inaktiv'}
                  </StatusBadge>
                </td>
                <td>
                  <ActionButtons>
                    <ActionButton 
                      className="edit" 
                      onClick={() => handleEdit(product)}
                    >
                      Redigera
                    </ActionButton>
                    <ActionButton 
                      className="delete" 
                      onClick={() => handleDelete(product)}
                    >
                      Ta bort
                    </ActionButton>
                  </ActionButtons>
                </td>
              </tr>
            ))}
          </tbody>
        </ProductTable>
      )}
      
      {showForm && (
        <ProductForm>
          <FormContent>
            <h3>{editingProduct ? 'Redigera produkt' : 'Lägg till produkt'}</h3>
            <form onSubmit={handleSave}>
              <div className="form-group">
                <label>Produktnamn *</label>
                <input 
                  type="text" 
                  value={formData.name}
                  onChange={(e) => handleFormChange('name', e.target.value)}
                  required
                />
                {formErrors.name && <div className="error-message">{formErrors.name}</div>}
              </div>
              
              <div className="form-group">
                <label>URL-slug *</label>
                <input 
                  type="text" 
                  value={formData.slug}
                  onChange={(e) => handleFormChange('slug', e.target.value)}
                  required
                />
                {formErrors.slug && <div className="error-message">{formErrors.slug}</div>}
              </div>
              
              <div className="form-group">
                <label>Kort beskrivning</label>
                <input 
                  type="text" 
                  value={formData.short_description}
                  onChange={(e) => handleFormChange('short_description', e.target.value)}
                  placeholder="Visas i produktlistor"
                />
              </div>
              
              <div className="form-group">
                <label>Beskrivning</label>
                <textarea 
                  value={formData.description}
                  onChange={(e) => handleFormChange('description', e.target.value)}
                  placeholder="Detaljerad produktbeskrivning"
                />
              </div>
              
              <ImageUpload
                images={formData.images}
                onImagesChange={handleImagesChange}
                maxImages={6}
              />
              
              <div className="form-row">
                <div className="form-group">
                  <label>Inköpspris (kr)</label>
                  <input 
                    type="number" 
                    step="0.01"
                    value={formData.cost_price}
                    onChange={(e) => handleFormChange('cost_price', e.target.value)}
                    placeholder="0.00"
                  />
                </div>
                
                <div className="form-group">
                  <label>Försäljningspris (kr) *</label>
                  <input 
                    type="number" 
                    step="0.01"
                    value={formData.price}
                    onChange={(e) => handleFormChange('price', e.target.value)}
                    required
                  />
                  {formErrors.price && <div className="error-message">{formErrors.price}</div>}
                </div>
              </div>
              
              {calculateMargins() && (
                <div className="margin-display">
                  <div className="margin-item">
                    <span>Vinst per enhet:</span>
                    <span>{calculateMargins()?.profit.toFixed(2)} kr</span>
                  </div>
                  <div className="margin-item">
                    <span>Pålägg:</span>
                    <span>{calculateMargins()?.markup}%</span>
                  </div>
                  <div className="margin-item">
                    <span>Marginal:</span>
                    <span>{calculateMargins()?.margin}%</span>
                  </div>
                </div>
              )}
              
              <div className="form-row">
                <div className="form-group">
                  <label>Jämförpris (kr)</label>
                  <input 
                    type="number" 
                    step="0.01"
                    value={formData.compare_price}
                    onChange={(e) => handleFormChange('compare_price', e.target.value)}
                    placeholder="Tidigare pris"
                  />
                </div>
                
                <div className="form-group">
                  <label>SKU (Artikelnummer) *</label>
                  <input 
                    type="text" 
                    value={formData.sku}
                    onChange={(e) => handleFormChange('sku', e.target.value)}
                    required
                    placeholder="Produktkod - välj från förslag nedan"
                  />
                  
                  {/* SKU Status */}
                  {formData.sku && (
                    <div className={`sku-status ${checkingSku ? 'checking' : formErrors.sku ? 'unavailable' : 'available'}`}>
                      {checkingSku ? (
                        <>⏳ Kontrollerar tillgänglighet...</>
                      ) : formErrors.sku ? (
                        <>❌ Redan använt</>
                      ) : (
                        <>✅ Tillgängligt</>
                      )}
                    </div>
                  )}
                  
                  {formErrors.sku && <div className="error-message">{formErrors.sku}</div>}
                  
                  {/* SKU Suggestions */}
                  {!editingProduct && suggestedSkus.length > 0 && (
                    <div className="sku-suggestions">
                      <div className="suggestions-label">💡 Föreslagna SKU-koder:</div>
                      <div className="suggestions-list">
                        {suggestedSkus.map((suggestion, index) => (
                          <div
                            key={index}
                            className={`suggestion-item ${formData.sku === suggestion ? 'selected' : ''}`}
                            onClick={() => {
                              handleFormChange('sku', suggestion);
                            }}
                          >
                            {suggestion}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
              
              <div className="form-row">
                <div className="form-group">
                  <label>Lagerantal *</label>
                  <input 
                    type="number" 
                    value={formData.inventory_quantity}
                    onChange={(e) => handleFormChange('inventory_quantity', e.target.value)}
                    required
                    min="0"
                  />
                  {formErrors.inventory_quantity && <div className="error-message">{formErrors.inventory_quantity}</div>}
                </div>
                
                <div className="form-group">
                  <label>Kategori</label>
                  <select
                    value={formData.category_ids[0] || ''}
                    onChange={(e) => handleFormChange('category_ids', e.target.value ? [parseInt(e.target.value)] : [])}
                  >
                    <option value="">Välj kategori</option>
                    {categories.map(category => (
                      <option key={category.id} value={category.id}>
                        {category.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              
              <div className="form-row">
                <div className="form-group">
                  <label>
                    <input 
                      type="checkbox" 
                      checked={formData.is_active}
                      onChange={(e) => handleFormChange('is_active', e.target.checked)}
                    />
                    {' '}Aktiv produkt
                  </label>
                </div>
                
                <div className="form-group">
                  <label>
                    <input 
                      type="checkbox" 
                      checked={formData.is_featured}
                      onChange={(e) => handleFormChange('is_featured', e.target.checked)}
                    />
                    {' '}Utvald produkt
                  </label>
                </div>
              </div>
              
              <div className="form-actions">
                <button 
                  type="button" 
                  className="cancel"
                  onClick={() => {
                    setShowForm(false);
                    setEditingProduct(null);
                    resetForm();
                  }}
                  disabled={saving}
                >
                  Avbryt
                </button>
                <button type="submit" className="save" disabled={saving}>
                  {saving ? 'Sparar...' : 'Spara'}
                </button>
              </div>
            </form>
          </FormContent>
        </ProductForm>
      )}
    </ProductsWrapper>
  );
};

export default AdminProducts;
